import { Component } from '@theme/component';
import { SlideshowSelectEvent, ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideSelect, { signal });
    if (this.presentation === 'carousel') {
      this.#preloadAdjacentImages();
    }
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
  #colorSelectTimer;
  #selectingColorFromGallery = false;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    clearTimeout(this.#colorSelectTimer);
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // The user is already looking at the requested photo. Replacing the gallery
    // would move that variant image to position 0 and trap arrow navigation.
    if (this.#selectingColorFromGallery) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /** Start fetching only the neighboring slides, so the next arrow/swipe doesn't reveal an unloaded image. */
  #preloadAdjacentImages = () => {
    const slides = this.slideshow?.slides;
    if (!slides?.length) return;

    const index = this.slideshow.current;
    for (const nextIndex of [index - 1, index + 1]) {
      const image = slides[nextIndex]?.querySelector('img.product-media__image');
      if (image instanceof HTMLImageElement && image.loading === 'lazy') {
        image.loading = 'eager';
      }
    }
  };

  /** Select only colors explicitly associated with a variant image, never generic detail images. */
  #handleSlideSelect = (event) => {
    if (event.target !== this.slideshow) return;
    this.#preloadAdjacentImages();
    if (!event.detail.userInitiated) return;

    clearTimeout(this.#colorSelectTimer);
    const optionValueId = event.detail.slide?.getAttribute('data-color-option-value-id');
    if (!optionValueId) return;

    // Wait for a swipe to settle; avoid requesting a new section for every intermediate image.
    this.#colorSelectTimer = setTimeout(() => {
      const section = this.closest('.shopify-section');
      const picker = Array.from(section?.querySelectorAll('variant-picker') ?? []).find(
        (element) => element.dataset.productId === this.dataset.productId && !element.closest('product-card')
      );
      if (!picker || !this.isConnected) return;

      const select = picker.querySelector('select[data-color-select]');
      const option = Array.from(select?.options ?? []).find((item) => item.dataset.optionValueId === optionValueId);
      if (select && option) {
        if (select.selectedOptions[0] === option) return;
        const dropdownItem = Array.from(picker.querySelectorAll('[data-color-dropdown] .color-dropdown-item')).find(
          (item) => item.dataset.optionValueId === optionValueId
        );
        this.#selectingColorFromGallery = true;
        try {
          if (dropdownItem instanceof HTMLButtonElement) dropdownItem.click();
          else {
            select.value = option.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } finally {
          this.#selectingColorFromGallery = false;
        }
        return;
      }

      const input = Array.from(picker.querySelectorAll('input[data-option-value-id]')).find(
        (item) => item.dataset.optionValueId === optionValueId
      );
      if (input instanceof HTMLInputElement && !input.checked) {
        this.#selectingColorFromGallery = true;
        try {
          input.click();
        } finally {
          this.#selectingColorFromGallery = false;
        }
      }
    }, 220);
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
